import { Component } from '@theme/component';

const AUTO_SUBMIT_DELAY = 350;
const drawerQuery = window.matchMedia('(max-width: 989px)');

/**
 * Collection page filters. The sidebar is a plain GET form, so filtering and sorting work without
 * JavaScript (the "Apply" button submits). Here the form submits itself on desktop when a value changes,
 * the sidebar becomes a drawer on small screens, brand lists get a search box and "show more", and the
 * price fields get a two-handle slider.
 *
 * @typedef {object} CollectionRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} sidebar
 * @property {HTMLElement} main
 * @property {HTMLButtonElement} [toggle]
 * @property {HTMLElement} [overlay]
 * @property {HTMLElement} announcer
 *
 * @extends {Component<CollectionRefs>}
 */
class TphCollectionComponent extends Component {
  requiredRefs = ['form', 'sidebar', 'main', 'announcer'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | undefined} */
  #submitTimer;
  #drawerOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { form, sidebar } = this.refs;

    form.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    sidebar.addEventListener('keydown', this.#onKeydown, { signal });
    drawerQuery.addEventListener('change', this.#onBreakpoint, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    this.#enhance();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    clearTimeout(this.#submitTimer);
    this.#setDrawer(false, false);
  }

  /** Turns on the script-only controls. Safe to run again after the DOM is replaced. */
  #enhance() {
    this.dataset.enhanced = 'true';

    for (const list of this.querySelectorAll('[data-filter-list]')) {
      if (!(list instanceof HTMLElement)) continue;
      const extras = list.querySelectorAll('[data-extra]');
      const hasActiveExtra = [...extras].some((item) => item.querySelector('input:checked'));
      const more = list.parentElement?.querySelector('[data-show-more]');
      if (!extras.length || !(more instanceof HTMLButtonElement)) continue;
      if (!hasActiveExtra && list.dataset.collapsed === undefined && list.dataset.touched === undefined) {
        list.dataset.collapsed = '';
      }
      more.hidden = false;
      this.#syncMore(list, more);
    }

    for (const search of this.querySelectorAll('[data-filter-search]')) {
      if (search instanceof HTMLElement) search.hidden = false;
    }
    for (const slider of this.querySelectorAll('[data-price-range]')) {
      if (slider instanceof HTMLElement) slider.hidden = false;
    }
    this.refs.toggle?.removeAttribute('hidden');
    for (const group of this.querySelectorAll('[data-price]')) this.#paintTrack(group);
  }

  /**
   * @param {HTMLElement} list
   * @param {HTMLButtonElement} more
   */
  #syncMore(list, more) {
    const collapsed = list.dataset.collapsed !== undefined;
    more.textContent = collapsed ? (more.dataset.more ?? '') : (more.dataset.less ?? '');
    more.setAttribute('aria-expanded', String(!collapsed));
  }

  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    // Restored from the back/forward cache: make sure the page isn't stuck behind a closed drawer
    if (event.persisted) this.#setDrawer(false, false);
  };

  #onBreakpoint = () => {
    if (!drawerQuery.matches) this.#setDrawer(false, false);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target instanceof HTMLInputElement && target.type === 'search') return;

    const inForm = target.closest('form') === this.refs.form || target.getAttribute('form') === this.refs.form.id;
    if (!inForm) return;

    // The drawer applies with its button; desktop applies right away
    if (drawerQuery.matches && target.getAttribute('form') !== this.refs.form.id) return;

    clearTimeout(this.#submitTimer);
    this.#submitTimer = window.setTimeout(() => this.refs.form.requestSubmit(), AUTO_SUBMIT_DELAY);
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.hasAttribute('data-filter-search')) {
      this.#search(target);
    } else if (target.hasAttribute('data-price-slider')) {
      this.#syncPriceFromSlider(target);
    } else if (target.hasAttribute('data-price-input')) {
      this.#syncSliderFromPrice(target);
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-open-filters]')) {
      this.#setDrawer(true, true);
      return;
    }
    if (target.closest('[data-close-filters]')) {
      this.#setDrawer(false, true);
      return;
    }

    const more = target.closest('[data-show-more]');
    if (more instanceof HTMLButtonElement) {
      const list = more.parentElement?.querySelector('[data-filter-list]');
      if (!(list instanceof HTMLElement)) return;
      list.dataset.touched = '';
      if (list.dataset.collapsed === undefined) list.dataset.collapsed = '';
      else delete list.dataset.collapsed;
      this.#syncMore(list, more);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#drawerOpen) {
      event.stopPropagation();
      this.#setDrawer(false, true);
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    clearTimeout(this.#submitTimer);
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    // Keep the URL clean: price fields left at their limits or empty aren't sent
    for (const group of form.querySelectorAll('[data-price]')) {
      const min = group.querySelector('[data-price-input="min"]');
      const max = group.querySelector('[data-price-input="max"]');
      if (!(min instanceof HTMLInputElement) || !(max instanceof HTMLInputElement)) continue;
      if (min.value === '' || min.value === min.dataset.bound) min.disabled = true;
      if (max.value === '' || max.value === max.dataset.bound) max.disabled = true;
    }
    // Re-enable on the next frame so the fields work after back navigation
    requestAnimationFrame(() => {
      for (const field of form.querySelectorAll('[data-price-input]')) {
        if (field instanceof HTMLInputElement) field.disabled = false;
      }
    });
  };

  /** @param {HTMLInputElement} input */
  #search(input) {
    const group = input.closest('[data-filter-group]');
    const list = group?.querySelector('[data-filter-list]');
    const more = group?.querySelector('[data-show-more]');
    if (!(list instanceof HTMLElement)) return;

    const query = input.value.trim().toLowerCase();
    let matches = 0;
    for (const item of list.children) {
      if (!(item instanceof HTMLElement)) continue;
      const text = item.textContent?.toLowerCase() ?? '';
      const match = !query || text.includes(query);
      item.hidden = !match;
      if (match) matches++;
    }

    // While searching, show every match; afterwards go back to the state the shopper had
    if (query) {
      list.dataset.searching = '';
      if (more instanceof HTMLElement) more.hidden = true;
    } else {
      delete list.dataset.searching;
      if (more instanceof HTMLElement) more.hidden = false;
    }

    const message = (input.dataset.matches ?? '').replace('[count]', String(matches));
    this.#announce(query ? message : '');
  }

  /** @param {HTMLInputElement} slider */
  #syncPriceFromSlider(slider) {
    const group = slider.closest('[data-price]');
    if (!group) return;
    const minSlider = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-price-slider="min"]'));
    const maxSlider = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-price-slider="max"]'));
    const minInput = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-price-input="min"]'));
    const maxInput = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-price-input="max"]'));
    if (!minSlider || !maxSlider || !minInput || !maxInput) return;

    // The handles can't cross
    if (Number(minSlider.value) > Number(maxSlider.value)) {
      if (slider === minSlider) minSlider.value = maxSlider.value;
      else maxSlider.value = minSlider.value;
    }
    minInput.value = minSlider.value;
    maxInput.value = maxSlider.value;
    this.#paintTrack(group);
  }

  /** @param {HTMLInputElement} input */
  #syncSliderFromPrice(input) {
    const group = input.closest('[data-price]');
    if (!group) return;
    const which = input.dataset.priceInput;
    const slider = /** @type {HTMLInputElement | null} */ (group.querySelector(`[data-price-slider="${which}"]`));
    const value = Number(input.value);
    if (!slider || input.value === '' || Number.isNaN(value)) return;

    // Typing is the source of truth, so only the slider follows; the typed text is left alone
    slider.value = String(value);
    this.#paintTrack(group);
  }

  /** @param {Element} group */
  #paintTrack(group) {
    if (!(group instanceof HTMLElement)) return;
    const minSlider = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-price-slider="min"]'));
    const maxSlider = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-price-slider="max"]'));
    if (!minSlider || !maxSlider) return;
    const low = Number(minSlider.min);
    const high = Number(minSlider.max);
    const span = high - low || 1;
    group.style.setProperty('--tph-col-price-start', `${((Number(minSlider.value) - low) / span) * 100}%`);
    group.style.setProperty('--tph-col-price-end', `${((Number(maxSlider.value) - low) / span) * 100}%`);
  }

  /**
   * @param {boolean} open
   * @param {boolean} moveFocus
   */
  #setDrawer(open, moveFocus) {
    const { sidebar, main, toggle } = this.refs;
    if (open === this.#drawerOpen) return;
    this.#drawerOpen = open;

    if (open) {
      sidebar.dataset.open = '';
      main.inert = true;
      document.documentElement.style.overflow = 'hidden';
      toggle?.setAttribute('aria-expanded', 'true');
      if (moveFocus) {
        const close = sidebar.querySelector('[data-close-filters]');
        if (close instanceof HTMLElement) close.focus();
      }
    } else {
      delete sidebar.dataset.open;
      main.inert = false;
      document.documentElement.style.overflow = '';
      toggle?.setAttribute('aria-expanded', 'false');
      if (moveFocus) toggle?.focus();
    }
  }

  /** @param {string} message */
  #announce(message) {
    const { announcer } = this.refs;
    announcer.textContent = '';
    if (!message) return;
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }
}

if (!customElements.get('tph-collection-component')) {
  customElements.define('tph-collection-component', TphCollectionComponent);
}
